import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import { Cookie, X } from 'lucide-react';

/**
 * Lightweight DSGVO cookie banner.
 *
 * Trackers we currently use:
 *   - Sentry (error monitoring). Needs consent (PII potential)
 *   - Stripe.js (loaded only on payment pages). Strictly necessary
 *   - Plausible self-hosted. Consent-free (no cookies, anonymized IPs)
 *
 * So the banner only really has to gate Sentry. But we structure it to
 * scale: a `getConsent()` helper exposes the choice as `{essential,
 * analytics, marketing}` flags, downstream components read the matching
 * flag before initializing their tracker.
 *
 * Local-only state. No server round-trip. Stored under
 * `real-content_cookie_consent` in localStorage. Versioned (`CONSENT_VERSION`)
 * so a material policy change can re-prompt the user.
 */

const STORAGE_KEY = 'real-content_cookie_consent';
const CONSENT_VERSION = 1;

// `decided` is the explicit "user has clicked something" sentinel.
// Earlier the encoding was "version === 0 means undecided", but that
// collides with policy bumps: incrementing CONSENT_VERSION to 2 made
// every previous decision look "undecided" again, which we never
// actually wanted. We only want a re-prompt on a material policy
// change, not a stealth one.
//
// Saved records always carry `decided: true` plus the version they
// agreed to. The re-prompt rule is now explicit: show the banner if
// the user hasn't decided yet, OR if their decision is on a stale
// version (left over from a real policy update).
const DEFAULT_CONSENT = {
  version: 0,
  decided: false,
  essential: true,  // always true; can't be disabled
  analytics: false,
  marketing: false,
  decided_at: null,
};

/** Public helper. Read consent state from anywhere. */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONSENT;
    const parsed = JSON.parse(raw);
    // Old records (pre-`decided`-sentinel) had `version === CONSENT_VERSION`
    // implicitly meaning decided. Treat them as decided so existing users
    // don't get re-prompted.
    if (parsed.decided === undefined) {
      parsed.decided = parsed.version === CONSENT_VERSION;
    }
    return parsed;
  } catch (_) {
    return DEFAULT_CONSENT;
  }
}

/** True iff the user needs to see the banner right now. */
export function needsConsentDecision() {
  const c = getConsent();
  return !c.decided || c.version !== CONSENT_VERSION;
}

function saveConsent(consent) {
  const enriched = {
    ...consent,
    version: CONSENT_VERSION,
    decided: true,
    decided_at: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(enriched));
  // Also broadcast so any in-memory subscribers (Sentry init, etc.) can
  // react without a page reload.
  window.dispatchEvent(new CustomEvent('real-content:consent-changed', { detail: enriched }));
  return enriched;
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({ analytics: false, marketing: false });

  // On mount: show the banner only if no decision was made under the
  // current policy version. We deliberately don't show again if the
  // user already decided. No dark-pattern re-prompts.
  useEffect(() => {
    if (needsConsentDecision()) {
      setVisible(true);
    }
  }, []);

  // Escape key = decline non-essential cookies (the strictest, most
  // privacy-respecting default). Modal-style accessibility expectation
  // for any dismissable overlay; without this, keyboard users have no
  // fast exit and the banner is the only thing standing between them
  // and the page content.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        saveConsent({ essential: true, analytics: false, marketing: false });
        setVisible(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const declineAll = () => {
    // Essential always stays true. The app would break without session
    // cookies. The DSGVO carve-out for "strictly necessary" cookies
    // covers these without consent.
    saveConsent({ essential: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent({ essential: true, analytics: consent.analytics, marketing: consent.marketing });
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 bg-[#fffdf7]/95 border-t border-[#e3dccb] backdrop-blur-md shadow-[0_-8px_24px_-16px_rgba(35,33,26,0.25)]"
      role="dialog"
      aria-live="polite"
      aria-label={t('cookies.bannerLabel') || 'Cookie-Einstellungen'}
      data-testid="cookie-banner"
    >
      <div className="max-w-6xl mx-auto">
        {!showSettings ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <Cookie className="w-5 h-5 text-[#1f4d3a] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-[#23211a]">
                  {t('cookies.message') ||
                    'Wir verwenden technisch notwendige Cookies, damit du eingeloggt bleibst. Optional dürfen wir Sentry zur Fehlerdiagnose laden, wenn du zustimmst.'}
                  {' '}
                  <Link to="/datenschutz" className="text-[#1f4d3a] hover:text-[#163a2c] underline">
                    {t('cookies.learnMore') || 'Mehr erfahren'}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowSettings(true)}
                className="text-[#6f6a5c] hover:text-[#23211a] text-xs"
                data-testid="cookie-settings-btn"
              >
                {t('cookies.settings') || 'Einstellungen'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={declineAll}
                className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db] text-xs"
                data-testid="cookie-decline-btn"
              >
                {t('cookies.declineAll') || 'Nur notwendige'}
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="bg-[#1f4d3a] hover:bg-[#163a2c] text-[#f7f3ea] text-xs"
                data-testid="cookie-accept-btn"
              >
                {t('cookies.acceptAll') || 'Alle akzeptieren'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-[#23211a]">
                {t('cookies.settingsTitle') || 'Cookie-Einstellungen'}
              </p>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="text-[#9c9585] hover:text-[#23211a]"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg bg-[#f2ecdf] border border-[#e3dccb] cursor-not-allowed opacity-70">
                <input type="checkbox" checked disabled className="mt-1" />
                <div>
                  <p className="text-sm text-[#23211a]">{t('cookies.essential') || 'Notwendig (immer aktiv)'}</p>
                  <p className="text-xs text-[#8a8474] mt-0.5">
                    {t('cookies.essentialDesc') || 'Login und Sitzungsverwaltung. Ohne diese funktioniert die App nicht.'}
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg bg-[#f2ecdf] border border-[#e3dccb] cursor-pointer hover:border-[#1f4d3a]/50">
                <input
                  type="checkbox"
                  checked={consent.analytics}
                  onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                  className="mt-1 accent-[#1f4d3a]"
                  data-testid="cookie-analytics-toggle"
                />
                <div>
                  <p className="text-sm text-[#23211a]">{t('cookies.analytics') || 'Fehler-Monitoring (Sentry)'}</p>
                  <p className="text-xs text-[#8a8474] mt-0.5">
                    {t('cookies.analyticsDesc') ||
                      'Hilft uns, Fehler in der App zu erkennen. Übermittelt anonymisierte Stack-Traces an Sentry.'}
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg bg-[#f2ecdf] border border-[#e3dccb] cursor-pointer hover:border-[#1f4d3a]/50">
                <input
                  type="checkbox"
                  checked={consent.marketing}
                  onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                  className="mt-1 accent-[#1f4d3a]"
                />
                <div>
                  <p className="text-sm text-[#23211a]">{t('cookies.marketing') || 'Marketing'}</p>
                  <p className="text-xs text-[#8a8474] mt-0.5">
                    {t('cookies.marketingDesc') ||
                      'Aktuell ungenutzt. Wenn wir später Pixel oder Werbe-Remarketing einsetzen, fragen wir hier.'}
                  </p>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                onClick={saveCustom}
                className="bg-[#1f4d3a] hover:bg-[#163a2c] text-[#f7f3ea] text-xs"
              >
                {t('cookies.saveChoices') || 'Auswahl speichern'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
